import type { AnchorHTMLAttributes } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { SUPPORTED_FIVERR_LOCALES_KEYS } from '@fiverr-private/localization';
import pathfinder from '@fiverr-private/pathfinder';
import { reportBigQuery } from '../utils/events';
import { LOGGED_OUT_HEADER_TYPE, LOGGED_OUT_SIDEBAR_TYPE } from './constants';

export const DEFAULT_BLOG_URL = 'http://blog.fiverr.com';
const GERMAN_SUFFIX = 'de';

export const BLOG_PAGE_URLS = {
    [(SUPPORTED_FIVERR_LOCALES_KEYS as Record<string, string>).DE_DE]: `${DEFAULT_BLOG_URL}/${GERMAN_SUFFIX}`,
};

interface GetResourcesLinksProps {
    headerType?: string;
    sidebarType?: string;
}

export interface ResourceLink {
    key: string;
    copyKeys: {
        name: string;
        caption: string;
    };
    href: string;
    anchorAttributes: {
        target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
        rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
    };
    isNew?: boolean;
    reportBQ: () => void;
}

const resourcesLinkMaker =
    (headerType?: string) =>
    ({
        key,
        href,
        withoutSource = false,
        nofollow = false,
        isNew = false,
    }: {
        key: string;
        href: string;
        withoutSource?: boolean;
        nofollow?: boolean;
        isNew?: boolean;
    }) => ({
        key,
        copyKeys: {
            name: `resources_tab.${key}.name`,
            caption: `resources_tab.${key}.caption`,
        },
        href: withoutSource ? href : `${href}?source=explore-tab`,
        isNew,
        anchorAttributes: {
            target: '_blank',
            rel: nofollow ? 'noopener nofollow' : 'noopener',
        },
        reportBQ: () => reportBigQuery(`resources_tab::${key}`, headerType),
    });

export const getResourcesLinks = ({ headerType, sidebarType }: GetResourcesLinksProps = {}): ResourceLink[] => {
    const { locale } = getContext();
    const blogUrl = BLOG_PAGE_URLS[locale] || DEFAULT_BLOG_URL;
    const makeResourceLink = resourcesLinkMaker(headerType || sidebarType);
    const showLogoMaker = sidebarType === LOGGED_OUT_SIDEBAR_TYPE || headerType === LOGGED_OUT_HEADER_TYPE;

    return [
        {
            key: 'answers',
            href: 'https://answers.fiverr.com',
            withoutSource: true,
        },
        {
            key: 'community',
            href: 'https://community.fiverr.com',
        },
        {
            key: 'guides',
            href: '/resources/guides', // pathfinder issue https://github.com/fiverr/pathfinder/issues/45
        },
        {
            key: 'blog',
            href: blogUrl,
        },
        {
            hidden: !showLogoMaker,
            key: 'logo_maker',
            href: pathfinder('logo_maker_buyer_landing_page'),
        },
    ]
        .filter(({ hidden }) => !hidden)
        .map(makeResourceLink);
};
